import { alphaSort, numericSort } from 'vet-bones/utils';

import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { Suggestion } from 'src/containers/ProjectMapping/types';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import {
  IgnoredSuggestion,
  getOrganizedSuggestionRecords,
  getResourceSuggestionKey,
} from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';

export const getMappingSuggestions = ({
  labelsByIri,
  mappings,
  selectedClasses,
  suggestions,
}: {
  labelsByIri: Record<string, string>;
  mappings: Mapping[];
  selectedClasses: string[];
  suggestions: Suggestion[];
}): {
  ignoredSuggestions: IgnoredSuggestion[];
  mappingSuggestions: MappingSuggestion[];
} => {
  const isClassAutoCreated = selectedClasses.length === 0;

  const {
    classSuggestions,
    attributeSuggestions,
    relationshipSuggestions,
    labelSuggestions,
    primaryIdentifierSuggestions,
    ignoredSuggestions,
  } = getOrganizedSuggestionRecords({
    isClassAutoCreated,
    labelsByIri,
    mappings,
    selectedClasses,
    suggestions,
  });

  const mappingSuggestions: MappingSuggestion[] = [];

  Object.entries(classSuggestions).forEach(([iriKey, suggestionGroup]) => {
    const { iri: classIri, label: classLabel, resource } = suggestionGroup;
    const attributeSuggestion = [
      ...(attributeSuggestions[iriKey]?.suggestions || []),
    ].sort(alphaSort(({ label, field }) => label || field));
    const relationshipSuggestion = [
      ...(relationshipSuggestions[iriKey]?.suggestions || []),
    ].sort(
      alphaSort(
        ({ label, fields }) =>
          label || fields.map((field) => field.value).join('')
      )
    );

    suggestionGroup.suggestions.forEach(({ source }) => {
      const sourceKey = getResourceSuggestionKey(resource.id, source);

      const primaryIdentifierSuggestion = [
        ...(primaryIdentifierSuggestions[sourceKey]?.suggestions || [])
          .sort(numericSort((item) => item.score, true))
          .slice(0, 1),
      ];
      const labelSuggestion = [
        ...(labelSuggestions[sourceKey]?.suggestions || [])
          .sort(numericSort((item) => item.score, true))
          .slice(0, 1),
      ];

      const allSuggestions = [
        ...primaryIdentifierSuggestion,
        ...labelSuggestion,
        ...relationshipSuggestion,
        ...attributeSuggestion,
      ];

      const suggestion = {
        name: `${resource.name} - ${classLabel}`,
        resourceId: resource.id,
        classIri,
        classLabel,
        isClassAutoCreated,
        isExpanded: false,
        suggestions: allSuggestions.map((suggestion) => ({
          ...suggestion,
          rejected: false,
          selected: false,
        })),
      };

      mappingSuggestions.push(suggestion);
    });
  });

  return {
    ignoredSuggestions,
    mappingSuggestions: mappingSuggestions.sort(
      alphaSort((suggestion) => suggestion.name)
    ),
  };
};
